import URI from 'urijs'
import { config } from '@/plugins/env.config'
import Vue from 'vue'
import { decamelizeKeys } from 'humps'

function generateDispensationRequestPayload(authorizationState, approvedProducts){
  const UMBRELLA_SPONSOR_VERIFICATIONS = {
    slug: "us-umbrella-corporation",
    externalVerifications : [
      {
        type: "SPONSOR_PROVIDER_CODE",
        value: "2342",
        verification_status: "SUCCESS"
      },
      {
        type: "SPONSOR_TRANSACTION_ID",
        value: generateSponsorTransactionId(),
        verification_status: "SUCCESS"
      }
    ]
  }

  const authorizationModule = authorizationState.authorization
  const prescription = authorizationModule.prescription
  const sponsorSlug = authorizationModule.newAuthorization.beneficiarySearch.sponsorSlug
  const authorizationCode = prescription.authorizations[0].code
  const currencyCode = authorizationModule.beneficiary.currencyCode || ''
  const products = generateApprovedProductsPayload(approvedProducts);
  const biometricVerifications = authorizationModule.biometricVerificationResult

  let dispensationPayloadValues = {
      currencyCode,
      products
  }

  if(sponsorSlug === UMBRELLA_SPONSOR_VERIFICATIONS.slug){
    const externalVerifications = UMBRELLA_SPONSOR_VERIFICATIONS.externalVerifications
    dispensationPayloadValues = { ...dispensationPayloadValues, externalVerifications }
  }

  if(biometricVerifications.type && biometricVerifications.id){
    const externalVerifications = [
      {
        type: biometricVerifications.type,
        value: biometricVerifications.id
      }
    ]
    dispensationPayloadValues = { ...dispensationPayloadValues, externalVerifications }
  }

  const dispensationRequest = {
    payload: dispensationPayloadValues,
    authorizationCode: authorizationCode
  }

  return dispensationRequest
}

async function startDispensation(authorizationCode, startDispensationPayload, attemptNumber = 1){
  const dispensingDocumentsURI = new URI(config.apiBaseUrl)
  dispensingDocumentsURI.segment('dispensing')
  dispensingDocumentsURI.segment('v1')
  dispensingDocumentsURI.segment('documents')
  dispensingDocumentsURI.segment(authorizationCode)
  dispensingDocumentsURI.segment('dispensations')

  const body = decamelizeKeys(startDispensationPayload)

  try {    
    const response = await Vue.prototype.$http.post(dispensingDocumentsURI.toString(), body)

    if(response.status === 202){
      const location = response.headers.location

      if(location){
        return await getDispensingMonitor(location);
      }
    }
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return startDispensation(authorizationCode, startDispensationPayload, attemptNumber + 1)
    }
    throw error
  }
}

async function updateDispensation(dispensingId, updateDispensationPayload, attemptNumber = 1){
  const dispensingDocumentsURI = new URI(config.apiBaseUrl)
  dispensingDocumentsURI.segment('dispensing')
  dispensingDocumentsURI.segment('v1')
  dispensingDocumentsURI.segment('dispensations')
  dispensingDocumentsURI.segment(dispensingId.toString())

  const body = decamelizeKeys(updateDispensationPayload)

  try {    
    return await Vue.prototype.$http.put(dispensingDocumentsURI.toString(), body)
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return updateDispensation(dispensingId, updateDispensationPayload, attemptNumber + 1)
    }
    throw error
  }
}

async function getDispensingMonitor(payload, attemptNumber = 1) {
  const monitorId = new URI(payload)

  try {
    const response = await Vue.prototype.$http.get(monitorId.toString())

    if (response.data.status === 'PENDING') {
      const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 2000))
      return getDispensingMonitor(payload)
    }

    if (response.data.status === 'ERROR') {
      throw response
    }

    return response
  } catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response ? error.response : error
    const status = response.status ? response.status : error.status

    if (status === 500 && attemptNumber < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
      return getDispensingMonitor(payload, attemptNumber + 1)
    }

    throw response.data
  }
}

async function completeDispensation(dispensingId, dispensationPayload, attemptNumber = 1){
  const completeDispensationURI = new URI(config.apiBaseUrl)
  completeDispensationURI.segment('dispensing')
  completeDispensationURI.segment('v1')
  completeDispensationURI.segment('dispensations')
  completeDispensationURI.segment(dispensingId.toString())
  completeDispensationURI.segment('complete')

  const body = decamelizeKeys(dispensationPayload)

  try {    
    return await Vue.prototype.$http.post(completeDispensationURI.toString(), body)
  }catch(error){
    const maxAttempts = config.getTokenMaxAttempts
    const attemptDelayInSeconds = config.getTokenAttemptDelayInSeconds

    const response = error.response
    if(response){
      const status = response.status
      if (status === 500 && attemptNumber < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, attemptDelayInSeconds * 1000))
        return completeDispensation(dispensingId, dispensationPayload, attemptNumber + 1)
      }
      throw response.data
    }
    else{
      throw error
    }
  }
}

function generateApprovedProductsPayload(approvedProducts){
  const products = [];
  if(approvedProducts){
    approvedProducts.map(product => {
      products.push({
        id: product.id,
        price: product.subtotal,
        quantity: product.quantity,
        taxes: "0.00"
      })
    })
  }
  return products;
}

function generateSponsorTransactionId(){
  const RANDOM_LENGTH_DIGITS = 10000000
  const RANDOM_NON_ZERO_FLAG = 90000000
  const randomTransactionId = Math.floor(RANDOM_LENGTH_DIGITS + Math.random() * RANDOM_NON_ZERO_FLAG)
  return randomTransactionId.toString()
}

export {
  startDispensation,
  updateDispensation,
  getDispensingMonitor,
  completeDispensation,
  generateApprovedProductsPayload,
  generateSponsorTransactionId,
  generateDispensationRequestPayload
}